import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, FileText, Trash2, GitCompareArrows, Mail, Check } from "lucide-react";
import { getAthlete, listTests, createTest, deleteTest, deleteAthlete, listEmailSends, updateAthlete } from "../lib/api";
import { DEFAULT_ZONES, DEFAULT_TIME_STAGES, DEFAULT_COACH_NOTE, ZONE_BENEFITS } from "../lib/constants";
import { toast } from "sonner";

function formatDate(s) {
  if (!s) return "—";
  try {
    // Date-only strings (YYYY-MM-DD) get constructed in LOCAL time so they
    // render as the entered day. UTC parsing shifts them back one day in
    // any timezone west of UTC.
    const m = typeof s === "string" && /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    const d = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(s);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return s;
  }
}

export default function AthleteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [emailSends, setEmailSends] = useState([]);
  // Snapshot of the last server-confirmed athlete record. The on-blur
  // `persistField` no-op check compares against this snapshot rather than the
  // optimistic `athlete` state (which is already updated by onChange, making
  // the snapshot necessary so the no-op check doesn't always short-circuit
  // and silently drop the save).
  const savedRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, t, sends] = await Promise.all([
        getAthlete(id),
        listTests(id),
        listEmailSends(id).catch(() => []),
      ]);
      setAthlete(a);
      savedRef.current = a;
      setTests(t);
      setEmailSends(sends);
    } catch {
      toast.error("Couldn't load athlete");
      navigate("/lactate");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [id]);

  const newTest = async () => {
    setCreating(true);
    try {
      const body = {
        athlete_id: id,
        // Default to TODAY in local time — `toISOString().slice(0,10)`
        // would jump to tomorrow for users west of UTC after 8 PM and
        // make coaches re-enter the date on every new test.
        test_date: (() => {
          const d = new Date();
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        })(),
        protocol: "Treadmill 1% Incline",
        speed_unit: "mph",
        rows: DEFAULT_TIME_STAGES.map((t) => ({
          time: t,
          pace: "",
          speed: null,
          hr: null,
          blood_lactate: null,
          rpe: null,
        })),
        lt1: {},
        lt2: {},
        zones: DEFAULT_ZONES,
        coach_note: DEFAULT_COACH_NOTE,
      };
      const test = await createTest(body);
      navigate(`/tests/${test.id}/edit`);
    } catch {
      toast.error("Failed to create test");
    } finally {
      setCreating(false);
    }
  };

  const onDeleteTest = async (tid) => {
    if (!window.confirm("Delete this test?")) return;
    await deleteTest(tid);
    toast.success("Test deleted");
    refresh();
  };

  const onDeleteAthlete = async () => {
    if (!window.confirm(`Delete ${athlete?.name} and all their tests?`)) return;
    await deleteAthlete(id);
    toast.success("Athlete deleted");
    navigate("/lactate");
  };

  const persistField = async (field, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    // Compare against the last server-confirmed value (not the optimistic
    // `athlete` state, which onChange has already mutated with `trimmed`).
    const lastSaved = savedRef.current?.[field];
    if (trimmed === lastSaved || (!trimmed && !lastSaved)) return;
    if (field === "name" && !trimmed) {
      toast.error("Name can't be empty");
      setAthlete((a) => ({ ...a, name: savedRef.current?.name || a.name }));
      return;
    }
    try {
      const updated = await updateAthlete(id, { [field]: trimmed || null });
      setAthlete(updated);
      savedRef.current = updated;
    } catch {
      toast.error(`Couldn't save ${field}`);
    }
  };

  if (loading || !athlete) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]">
        Loading…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10" data-testid="athlete-detail-page">
      <Link
        to="/lactate"
        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors mb-6"
        data-testid="back-to-athletes"
      >
        <ArrowLeft size={14} /> All athletes
      </Link>

      <header className="border-b border-[var(--tir-ink)] pb-8 mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="flex-1">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-3">
            Athlete file
          </div>
          <input
            value={athlete.name}
            onChange={(e) => setAthlete({ ...athlete, name: e.target.value })}
            onBlur={() => persistField("name", athlete.name)}
            data-testid="athlete-name-input"
            className="font-serif-display text-4xl sm:text-5xl text-[var(--tir-ink)] leading-tight bg-transparent w-full focus:outline-none focus:border-b focus:border-[var(--tir-burgundy)]"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-4 max-w-xl">
            <label className="block">
              <span className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-0.5">
                Email
              </span>
              <input
                type="email"
                value={athlete.email || ""}
                onChange={(e) => setAthlete({ ...athlete, email: e.target.value })}
                onBlur={() => persistField("email", athlete.email)}
                data-testid="athlete-email-input"
                placeholder="add@laterok.com"
                className="w-full bg-transparent border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-sm font-mono-tir focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-0.5">
                Phone
              </span>
              <input
                type="tel"
                value={athlete.phone || ""}
                onChange={(e) => setAthlete({ ...athlete, phone: e.target.value })}
                onBlur={() => persistField("phone", athlete.phone)}
                data-testid="athlete-phone-input"
                placeholder="+1 555-555-5555"
                className="w-full bg-transparent border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-sm font-mono-tir focus:outline-none"
              />
            </label>
          </div>
          <label className="block mt-3 max-w-xl">
            <span className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-0.5">
              Notes
            </span>
            <textarea
              value={athlete.notes || ""}
              onChange={(e) => setAthlete({ ...athlete, notes: e.target.value })}
              onBlur={() => persistField("notes", athlete.notes)}
              data-testid="athlete-notes-input"
              rows={2}
              placeholder="anything you want to remember about this athlete"
              className="w-full bg-transparent border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-sm focus:outline-none resize-none"
            />
          </label>
        </div>
        <div className="flex items-center gap-3">
          {tests.length >= 2 && (
            <Link
              to={`/athletes/${id}/compare`}
              data-testid="compare-tests-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
            >
              <GitCompareArrows size={14} /> Compare
            </Link>
          )}
          <button
            onClick={newTest}
            disabled={creating}
            data-testid="new-test-btn"
            className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-5 py-2.5 text-sm tracking-wider uppercase font-medium hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-50"
          >
            <Plus size={16} /> {creating ? "Creating…" : "New test"}
          </button>
          <button
            onClick={onDeleteAthlete}
            data-testid="delete-athlete-btn"
            className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors"
          >
            Delete
          </button>
        </div>
      </header>

      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif-display text-2xl">Test history</h2>
          <span className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)]">
            {tests.length} test{tests.length === 1 ? "" : "s"}
          </span>
        </div>
        {tests.length === 0 ? (
          <div className="border border-dashed border-[var(--tir-border)] py-16 text-center">
            <div className="font-serif-display text-xl text-[var(--tir-ink)]">No tests yet</div>
            <div className="text-sm text-[var(--tir-muted)] mt-2">
              Create the first lactate test for {athlete.name}.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-[var(--tir-border)] border-t border-b border-[var(--tir-border)]">
            {tests.map((t) => {
              const lastSend = emailSends.find((s) => s.test_id === t.id);
              return (
              <li key={t.id} className="grid grid-cols-12 gap-4 py-5 px-2 items-center" data-testid={`test-row-${t.id}`}>
                <div className="col-span-12 md:col-span-5">
                  <div className="font-serif-display text-xl text-[var(--tir-ink)]">
                    {formatDate(t.test_date)}
                  </div>
                  <div className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] mt-1">
                    {t.protocol} · {t.speed_unit.toUpperCase()}
                  </div>
                  {lastSend && (
                    <div
                      className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[var(--tir-burgundy)] mt-2 font-semibold"
                      data-testid={`test-sent-badge-${t.id}`}
                    >
                      <Check size={12} /> Sent {formatDate(lastSend.sent_at)}
                      {lastSend.recipient ? (
                        <span className="text-[var(--tir-muted)] font-normal normal-case tracking-normal">
                          {" "}· {lastSend.recipient}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="col-span-6 md:col-span-3 text-sm font-mono-tir text-[var(--tir-ink)]">
                  {t.lt1?.speed ? (
                    <>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                        LT1
                      </div>
                      <div>
                        {t.lt1.speed} {t.speed_unit} · {t.lt1.hr || "—"} bpm
                      </div>
                    </>
                  ) : (
                    <span className="text-[var(--tir-muted)] text-xs uppercase tracking-[0.2em]">
                      No LT1
                    </span>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2 text-sm font-mono-tir text-[var(--tir-ink)]">
                  {t.lt2?.speed ? (
                    <>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                        LT2
                      </div>
                      <div>
                        {t.lt2.speed} {t.speed_unit}
                      </div>
                    </>
                  ) : (
                    <span className="text-[var(--tir-muted)] text-xs uppercase tracking-[0.2em]">
                      No LT2
                    </span>
                  )}
                </div>
                <div className="col-span-12 md:col-span-2 flex items-center justify-end gap-3 flex-wrap">
                  <Link
                    to={`/tests/${t.id}/edit`}
                    data-testid={`edit-test-${t.id}`}
                    className="text-xs tracking-[0.2em] uppercase text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] transition-colors"
                  >
                    Edit
                  </Link>
                  <Link
                    to={`/tests/${t.id}/report`}
                    data-testid={`report-test-${t.id}`}
                    className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase text-[var(--tir-ink)] hover:text-[var(--tir-burgundy)] transition-colors"
                  >
                    <FileText size={13} /> Report
                  </Link>
                  <Link
                    to={`/tests/${t.id}/report?send=1`}
                    data-testid={`send-test-${t.id}`}
                    title={lastSend ? "Send again" : "Send to athlete"}
                    className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase text-[var(--tir-ink)] hover:text-[var(--tir-burgundy)] transition-colors"
                  >
                    <Mail size={13} /> {lastSend ? "Send again" : "Send"}
                  </Link>
                  <button
                    onClick={() => onDeleteTest(t.id)}
                    data-testid={`delete-test-${t.id}`}
                    className="text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors"
                    aria-label="Delete test"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
